import React from "react";
import styled, { keyframes } from "styled-components";
import {
  Clock3,
  Coffee,
  Pause,
  Play,
  RefreshCcw,
  SkipForward,
  Smartphone,
  Target,
} from "lucide-react";
import { useFocusTimer, FOCUS_STAGE_META, formatFocusTime } from "../context/FocusTimerContext";

const Page = styled.main`
  min-height: 100vh;
  padding: 116px 20px 80px;
  color: #e5e7eb;
  background:
    radial-gradient(820px at 14% 8%, rgba(245, 158, 11, 0.12), transparent 60%),
    radial-gradient(860px at 84% 12%, rgba(56, 189, 248, 0.14), transparent 62%),
    radial-gradient(900px at 50% 100%, rgba(34, 197, 94, 0.06), transparent 68%),
    #020617;
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

const Hero = styled.section`
  padding: 30px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(280px, 0.7fr);
  gap: 20px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 16px 0 0;
  font-size: clamp(38px, 5vw, 56px);
  line-height: 1.08;
  font-weight: 1000;
  color: #f8fafc;
`;

const Sub = styled.p`
  margin: 18px 0 0;
  max-width: 720px;
  color: #94a3b8;
  font-size: 17px;
  line-height: 1.9;
`;

const HeroList = styled.div`
  margin-top: 18px;
  display: grid;
  gap: 10px;
`;

const HeroPoint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #cbd5e1;
  font-size: 14px;
`;

const HeroCard = styled.div`
  padding: 20px;
  border-radius: 24px;
  background:
    linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(56, 189, 248, 0.08)),
    rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(245, 158, 11, 0.18);
`;

const HeroCardLabel = styled.div`
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const HeroCardValue = styled.div`
  margin-top: 12px;
  color: #f8fafc;
  font-size: 28px;
  line-height: 1.15;
  font-weight: 1000;
`;

const HeroCardText = styled.div`
  margin-top: 8px;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.7;
`;

const MainGrid = styled.section`
  margin-top: 24px;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(320px, 0.7fr);
  gap: 20px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  padding: 24px;
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(8, 16, 29, 0.92), rgba(5, 11, 22, 0.9));
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.18);
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
`;

const PanelTitle = styled.h2`
  margin: 6px 0 0;
  color: #f8fafc;
  font-size: 1.6rem;
`;

const PanelEyebrow = styled.div`
  color: #7dd3fc;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 800;
`;

const StagePills = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const StagePill = styled.button`
  min-height: 42px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid
    ${({ $active, $tone }) => ($active ? $tone : "rgba(148, 163, 184, 0.14)")};
  background: ${({ $active, $soft }) =>
    $active ? `linear-gradient(135deg, ${$soft}, rgba(15, 23, 42, 0.88))` : "rgba(15, 23, 42, 0.42)"};
  color: ${({ $active }) => ($active ? "#f8fafc" : "#cbd5e1")};
  font-size: 13px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const TimerShell = styled.div`
  margin-top: 12px;
  padding: 28px;
  border-radius: 28px;
  background:
    radial-gradient(circle at top, rgba(56, 189, 248, 0.08), transparent 48%),
    rgba(2, 6, 23, 0.62);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const TimerMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const TimerLabel = styled.div`
  color: #f8fafc;
  font-size: 18px;
  font-weight: 900;
`;

const TimerHint = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.7;
`;

const tickReveal = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-0.9em);
    filter: blur(3px);
    text-shadow: 0 0 0 rgba(125, 211, 252, 0);
  }

  55% {
    opacity: 0.72;
    transform: translateY(0.08em);
    filter: blur(0.6px);
    text-shadow: 0 0 16px rgba(125, 211, 252, 0.14);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
    text-shadow: 0 0 0 rgba(125, 211, 252, 0);
  }
`;

const TimerReadout = styled.div`
  margin: 28px 0 22px;
  text-align: center;
  color: #f8fafc;
  font-size: clamp(64px, 10vw, 108px);
  font-weight: 1000;
  letter-spacing: -0.04em;
  line-height: 0.95;
  text-shadow: 0 0 28px rgba(125, 211, 252, 0.12);
  animation: ${tickReveal} 320ms ease;
`;

const TimeDigits = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.02em;
`;

const TimeChar = styled.span`
  display: inline-block;
  min-width: ${({ $isDivider }) => ($isDivider ? "0.34em" : "0.58em")};
  will-change: transform, opacity, filter;
  animation: ${({ $changed }) => ($changed ? tickReveal : "none")} 380ms cubic-bezier(0.22, 1, 0.36, 1);
  animation-delay: ${({ $changed, $index }) => ($changed ? `${$index * 45}ms` : "0ms")};
  animation-fill-mode: ${({ $changed }) => ($changed ? "both" : "none")};
`;

const ProgressTrack = styled.div`
  height: 12px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $percent }) => `${$percent}%`};
  border-radius: 999px;
  background: ${({ $gradient }) => $gradient};
  transition: width 1s linear;
`;

const TimerActions = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled.button`
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  font-weight: 900;
  color: #07111f;
  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SecondaryBtn = styled.button`
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(15, 23, 42, 0.58);
  color: #e2e8f0;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const StatsGrid = styled.div`
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  padding: 16px;
  border-radius: 20px;
  background: rgba(15, 23, 42, 0.56);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const StatLabel = styled.div`
  color: #94a3b8;
  font-size: 12px;
  font-weight: 800;
`;

const StatValue = styled.div`
  margin-top: 8px;
  color: #f8fafc;
  font-size: 1.6rem;
  font-weight: 1000;
`;

const Checklist = styled.div`
  display: grid;
  gap: 12px;
`;

const CheckItem = styled.div`
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.1);
  color: #cbd5e1;
  font-size: 14px;
  line-height: 1.65;
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const SessionFeed = styled.div`
  margin-top: 18px;
  display: grid;
  gap: 10px;
`;

const SessionItem = styled.div`
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(2, 6, 23, 0.42);
  border: 1px solid rgba(148, 163, 184, 0.1);
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
`;

const SessionText = styled.div`
  color: #e2e8f0;
  font-size: 14px;
  font-weight: 700;
`;

const SessionTime = styled.div`
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
`;

const EmptyState = styled.div`
  padding: 18px;
  border-radius: 20px;
  background: rgba(15, 23, 42, 0.42);
  border: 1px dashed rgba(148, 163, 184, 0.18);
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.7;
`;

export default function FocusWithoutPhonePage() {
  const {
    state,
    stageMeta,
    progressPercent,
    nextMilestone,
    setStage,
    toggleTimer,
    resetCurrentStage,
    skipStage,
  } = useFocusTimer();

  const Icon = state.stage === "work" ? Target : Coffee;
  const timeText = formatFocusTime(state.remainingSeconds);
  const previousTimeRef = React.useRef(timeText);
  const previousTimeText = previousTimeRef.current;

  React.useEffect(() => {
    previousTimeRef.current = timeText;
  }, [timeText]);

  return (
    <Page>
      <Container>
        <Hero>
          <HeroGrid>
            <div>
              <Eyebrow>
                <Smartphone size={14} />
                Focus without phone
              </Eyebrow>
              <Title>Work in 25-minute blocks, not random distracted sessions</Title>
              <Sub>
                This page gives you a simple Pomodoro rhythm: one focused 25-minute
                block, then a short reset. Keep the phone away, pick one task,
                and let consistency beat motivation.
              </Sub>

              <HeroList>
                <HeroPoint>
                  <Target size={16} />
                  One task only during the block
                </HeroPoint>
                <HeroPoint>
                  <Clock3 size={16} />
                  25 minutes work, 5 minutes break
                </HeroPoint>
                <HeroPoint>
                  <Coffee size={16} />
                  Longer break after every 4 focus blocks
                </HeroPoint>
              </HeroList>
            </div>

            <HeroCard>
              <HeroCardLabel>Today&apos;s target</HeroCardLabel>
              <HeroCardValue>{state.todayCompletedSessions} focused blocks</HeroCardValue>
              <HeroCardText>
                Finish even two strong sessions and the day already feels
                different. Protect attention first, then chase volume.
              </HeroCardText>
            </HeroCard>
          </HeroGrid>
        </Hero>

        <MainGrid>
          <Panel>
            <PanelHeader>
              <div>
                <PanelEyebrow>Pomodoro</PanelEyebrow>
                <PanelTitle>Your current session</PanelTitle>
              </div>
            </PanelHeader>

              <StagePills>
              {Object.entries(FOCUS_STAGE_META).map(([key, meta]) => {
                const StageIcon = key === "work" ? Target : Coffee;
                return (
                  <StagePill
                    key={key}
                    $active={state.stage === key}
                    $tone={meta.tone}
                    $soft={meta.soft}
                    onClick={() => setStage(key)}
                  >
                    <StageIcon size={14} />
                    {meta.label}
                  </StagePill>
                );
              })}
            </StagePills>

            <TimerShell>
              <TimerMeta>
                <div>
                  <TimerLabel>
                    <Icon size={16} style={{ marginRight: 8 }} />
                    {stageMeta.label}
                  </TimerLabel>
                  <TimerHint>{stageMeta.hint}</TimerHint>
                </div>
              </TimerMeta>

              <TimerReadout>
                <TimeDigits>
                  {timeText.split("").map((char, index) => (
                    <TimeChar
                      key={`${index}-${char === ":" ? "divider" : "digit"}`}
                      $index={index}
                      $isDivider={char === ":"}
                      $changed={previousTimeText[index] !== char}
                    >
                      {char}
                    </TimeChar>
                  ))}
                </TimeDigits>
              </TimerReadout>

              <ProgressTrack>
                <ProgressFill
                  $percent={progressPercent}
                  $gradient={stageMeta.gradient}
                />
              </ProgressTrack>

              <TimerActions>
                <PrimaryBtn onClick={toggleTimer}>
                  {state.isRunning ? <Pause size={16} /> : <Play size={16} />}
                  {state.isRunning ? "Pause session" : "Start session"}
                </PrimaryBtn>
                <SecondaryBtn onClick={resetCurrentStage}>
                  <RefreshCcw size={16} />
                  Reset
                </SecondaryBtn>
                <SecondaryBtn onClick={skipStage}>
                  <SkipForward size={16} />
                  Skip stage
                </SecondaryBtn>
              </TimerActions>
            </TimerShell>

            <StatsGrid>
              <StatCard>
                <StatLabel>Completed today</StatLabel>
                <StatValue>{state.todayCompletedSessions}</StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>Total work blocks</StatLabel>
                <StatValue>{state.completedWorkSessions}</StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>Next milestone</StatLabel>
                <StatValue style={{ fontSize: "1rem", lineHeight: 1.5 }}>
                  {nextMilestone}
                </StatValue>
              </StatCard>
            </StatsGrid>
          </Panel>

          <Panel>
            <PanelHeader>
              <div>
                <PanelEyebrow>Rules</PanelEyebrow>
                <PanelTitle>How to make it work</PanelTitle>
              </div>
            </PanelHeader>

            <Checklist>
              <CheckItem>
                <Smartphone size={16} />
                Keep the phone physically away from the desk before pressing
                start. Friction matters more than willpower.
              </CheckItem>
              <CheckItem>
                <Target size={16} />
                Decide one exact task before each block. Do not begin a session
                with a vague target.
              </CheckItem>
              <CheckItem>
                <Coffee size={16} />
                Breaks are for recovery, not doom-scrolling. Water, stretch,
                breathe, then return.
              </CheckItem>
            </Checklist>

            <PanelHeader style={{ marginTop: 24 }}>
              <div>
                <PanelEyebrow>Recent activity</PanelEyebrow>
                <PanelTitle>Today&apos;s log</PanelTitle>
              </div>
            </PanelHeader>

            {state.activity.length ? (
              <SessionFeed>
                {state.activity.map((item) => (
                  <SessionItem key={item.id}>
                    <SessionText>{item.label}</SessionText>
                    <SessionTime>{item.timestamp}</SessionTime>
                  </SessionItem>
                ))}
              </SessionFeed>
            ) : (
              <EmptyState>
                No completed focus block yet today. Start one 25-minute session
                and let the first clean win change the day.
              </EmptyState>
            )}
          </Panel>
        </MainGrid>
      </Container>
    </Page>
  );
}
